import referenceTranslations from '../../docs/reference/base44-translations.snapshot.json';

export type GlobalLocale = 'en' | 'ko' | 'ja' | 'zh';
export type GlobalContent = typeof referenceTranslations.en;

export interface SafeFact {
  value: string;
  countTo?: number;
  suffix?: string;
  label: string;
  note: string;
  source?: { label: string; href: string };
}

export interface TrustPrinciple {
  title: string;
  detail: string;
}

export interface GlobalSafeContent extends GlobalContent {
  safeFacts: SafeFact[];
  trust: {
    label: string;
    heading: string;
    items: TrustPrinciple[];
  };
  utility: {
    skip: string;
    close: string;
    language: string;
    required: string;
    consent: string;
    consentError: string;
    emailError: string;
    dateError: string;
    nameError: string;
    formDisclaimer: string;
    leadReference: string;
    privacy: string;
  };
}

export const localeRoutes: Record<GlobalLocale, string> = {
  en: '/',
  ko: '/ko/',
  ja: '/ja/',
  zh: '/zh-hans/'
};

export const localeMeta: Record<GlobalLocale, { label: string; short: string; lang: string; ogLocale: string }> = {
  en: { label: 'English', short: 'EN', lang: 'en', ogLocale: 'en_GB' },
  ko: { label: '한국어', short: 'KO', lang: 'ko', ogLocale: 'ko_KR' },
  ja: { label: '日本語', short: 'JA', lang: 'ja', ogLocale: 'ja_JP' },
  zh: { label: '简体中文', short: '中文', lang: 'zh-Hans', ogLocale: 'zh_CN' }
};

export function getLocaleFromPath(pathname = window.location.pathname): GlobalLocale {
  if (pathname === '/ko' || pathname.startsWith('/ko/')) return 'ko';
  if (pathname === '/ja' || pathname.startsWith('/ja/')) return 'ja';
  if (pathname === '/zh-hans' || pathname.startsWith('/zh-hans/')) return 'zh';
  return 'en';
}

const safeFacts: Record<GlobalLocale, SafeFact[]> = {
  en: [
    {
      value: '12M+', countTo: 12, suffix: 'M+',
      label: 'Children born worldwide through assisted reproductive technology',
      note: 'ICMART reports that ART has helped at least twelve million children be born worldwide.',
      source: { label: 'ICMART World Report', href: 'https://academic.oup.com/humrep/article/39/Supplement_1/deae108.137/7704088' }
    },
    {
      value: '1 in 6',
      label: 'Adults experience infertility during their lifetime',
      note: 'The World Health Organization estimate underscores how common the need for fertility care is.',
      source: { label: 'World Health Organization', href: 'https://www.who.int/news/item/04-04-2023-1-in-6-people-globally-affected-by-infertility' }
    },
    { value: 'Country-specific', label: 'Treatment access and parentage rules', note: 'Eligibility, donor rules, parentage, citizenship and return travel depend on the countries and people involved.' },
    { value: 'Costs vary', label: 'Across clinics, programmes and destinations', note: 'Compare the same medical scope, exclusions, legal work, travel and contingency costs before deciding.' }
  ],
  ko: [
    {
      value: '12M+', countTo: 12, suffix: 'M+',
      label: '보조생식술을 통해 전 세계에서 태어난 아이들',
      note: 'ICMART는 보조생식술을 통해 전 세계에서 최소 1,200만 명의 아이가 태어났다고 보고했습니다.',
      source: { label: 'ICMART 세계 보고서', href: 'https://academic.oup.com/humrep/article/39/Supplement_1/deae108.137/7704088' }
    },
    {
      value: '6명 중 1명',
      label: '평생 동안 난임을 경험하는 성인',
      note: '세계보건기구의 추정치는 생식 의료가 얼마나 보편적인 필요인지 보여줍니다.',
      source: { label: '세계보건기구', href: 'https://www.who.int/news/item/04-04-2023-1-in-6-people-globally-affected-by-infertility' }
    },
    { value: '국가별 확인', label: '치료 접근성과 친권 규정', note: '자격, 기증 규정, 친권, 국적 및 귀국 절차는 관련 국가와 개인 상황에 따라 달라집니다.' },
    { value: '비용은 다양', label: '병원·프로그램·목적지별 차이', note: '동일한 의료 범위, 제외 항목, 법률, 여행 및 예비 비용을 기준으로 비교해야 합니다.' }
  ],
  ja: [
    {
      value: '12M+', countTo: 12, suffix: 'M+',
      label: '生殖補助医療により世界で生まれた子どもたち',
      note: 'ICMARTは、生殖補助医療によって世界で少なくとも1,200万人の子どもが誕生したと報告しています。',
      source: { label: 'ICMART世界報告', href: 'https://academic.oup.com/humrep/article/39/Supplement_1/deae108.137/7704088' }
    },
    {
      value: '6人に1人',
      label: '生涯に不妊を経験する成人',
      note: '世界保健機関の推計は、生殖医療の必要性が決して珍しくないことを示しています。',
      source: { label: '世界保健機関', href: 'https://www.who.int/news/item/04-04-2023-1-in-6-people-globally-affected-by-infertility' }
    },
    { value: '国別に確認', label: '治療アクセスと親子関係の規則', note: '適格性、提供ルール、親子関係、国籍、帰国手続は、関係する国と個別事情により異なります。' },
    { value: '費用は多様', label: '施設・プログラム・渡航先による違い', note: '同じ医療範囲、除外項目、法務、渡航、予備費をそろえて比較することが重要です。' }
  ],
  zh: [
    {
      value: '12M+', countTo: 12, suffix: 'M+',
      label: '通过辅助生殖技术在全球出生的孩子',
      note: 'ICMART报告显示，辅助生殖技术已帮助全球至少一千二百万名孩子出生。',
      source: { label: 'ICMART世界报告', href: 'https://academic.oup.com/humrep/article/39/Supplement_1/deae108.137/7704088' }
    },
    {
      value: '每6人中1人',
      label: '成年人一生中会经历不孕不育',
      note: '世界卫生组织的估算说明，生殖医疗需求并不罕见。',
      source: { label: '世界卫生组织', href: 'https://www.who.int/news/item/04-04-2023-1-in-6-people-globally-affected-by-infertility' }
    },
    { value: '逐国确认', label: '治疗准入与亲权规则', note: '资格、捐赠规则、亲权、国籍与返程程序取决于相关国家和个人情况。' },
    { value: '费用不一', label: '诊所、项目与目的地之间的差异', note: '决定前应按相同医疗范围、排除项、法律、旅行与预备费用进行比较。' }
  ]
};

const trust: Record<GlobalLocale, GlobalSafeContent['trust']> = {
  en: {
    label: 'Guidance principles', heading: 'Clarity before commitment',
    items: [
      { title: 'Evidence before advertising', detail: 'We separate published evidence from provider marketing and mark questions that still need specialist confirmation.' },
      { title: 'One scope, compared fairly', detail: 'Medical, laboratory, legal, travel and contingency costs are compared on the same basis.' },
      { title: 'Your facts determine the route', detail: 'Family structure, medical history, passports and parentage goals are reviewed before a destination is shortlisted.' }
    ]
  },
  ko: {
    label: '가이드 원칙', heading: '결정하기 전에 먼저 명확하게',
    items: [
      { title: '광고보다 근거', detail: '공개 근거와 기관의 홍보를 구분하고, 전문가 확인이 필요한 질문을 명확히 표시합니다.' },
      { title: '같은 범위로 공정하게 비교', detail: '의료, 실험실, 법률, 여행 및 예비 비용을 동일한 기준으로 비교합니다.' },
      { title: '당신의 상황이 경로를 결정합니다', detail: '가족 구성, 병력, 여권과 친권 목표를 먼저 검토한 뒤 목적지를 추립니다.' }
    ]
  },
  ja: {
    label: 'ガイダンス原則', heading: '契約の前に、明確さを',
    items: [
      { title: '広告よりエビデンス', detail: '公開された根拠と機関の宣伝を分け、専門家の確認が必要な点を明示します。' },
      { title: '同じ範囲で公平に比較', detail: '医療、ラボ、法務、渡航、予備費を同じ条件で比較します。' },
      { title: 'あなたの事情が経路を決める', detail: '家族構成、病歴、旅券、親子関係の目標を確認してから候補地を絞ります。' }
    ]
  },
  zh: {
    label: '引导原则', heading: '承诺之前，先把事情看清',
    items: [
      { title: '证据先于广告', detail: '我们区分公开证据与机构宣传，并明确标出仍需专业人员确认的问题。' },
      { title: '同一范围，公平比较', detail: '医疗、实验室、法律、旅行与预备费用按相同口径比较。' },
      { title: '你的情况决定路径', detail: '先复核家庭结构、病史、护照与亲权目标，再筛选目的地。' }
    ]
  }
};

const utility: Record<GlobalLocale, GlobalSafeContent['utility']> = {
  en: {
    skip: 'Skip to content', close: 'Close', language: 'Select language', required: 'Required', consent: 'I agree that IVFToday may use these details to respond to my consultation request.',
    consentError: 'Please confirm your consent before submitting.', emailError: 'Enter a valid email address.', dateError: 'Choose a consultation date.', nameError: 'Enter your full name.',
    formDisclaimer: 'Do not submit medical records or highly sensitive health information here. This request does not confirm treatment eligibility, availability or outcome.', leadReference: 'Request reference', privacy: 'Your details are used only to respond to this request.'
  },
  ko: {
    skip: '본문으로 건너뛰기', close: '닫기', language: '언어 선택', required: '필수', consent: 'IVFToday가 상담 요청에 답변하기 위해 이 정보를 사용하는 데 동의합니다.',
    consentError: '제출 전에 동의를 확인해 주세요.', emailError: '올바른 이메일 주소를 입력해 주세요.', dateError: '상담 희망일을 선택해 주세요.', nameError: '성명을 입력해 주세요.',
    formDisclaimer: '의료기록이나 매우 민감한 건강정보를 제출하지 마세요. 이 요청은 치료 자격, 가능 여부 또는 결과를 확정하지 않습니다.', leadReference: '요청 번호', privacy: '입력하신 정보는 이 요청에 답변하는 용도로만 사용됩니다.'
  },
  ja: {
    skip: '本文へ移動', close: '閉じる', language: '言語を選択', required: '必須', consent: 'IVFTodayが相談依頼に回答するため、この情報を使用することに同意します。',
    consentError: '送信前に同意をご確認ください。', emailError: '有効なメールアドレスを入力してください。', dateError: '相談希望日を選択してください。', nameError: '氏名を入力してください。',
    formDisclaimer: '医療記録や機微な健康情報は送信しないでください。この依頼は治療適格性、受入可否、結果を確約するものではありません。', leadReference: '受付番号', privacy: '入力情報はこの依頼への回答にのみ使用します。'
  },
  zh: {
    skip: '跳到主要内容', close: '关闭', language: '选择语言', required: '必填', consent: '我同意IVFToday使用这些信息回复我的咨询请求。',
    consentError: '提交前请确认同意。', emailError: '请输入有效的邮箱。', dateError: '请选择咨询日期。', nameError: '请输入姓名。',
    formDisclaimer: '请勿在此提交医疗记录或高度敏感的健康信息。本请求不确认治疗资格、可用性或结果。', leadReference: '请求编号', privacy: '您的信息仅用于回复本次请求。'
  }
};

const countryOrientation: Record<GlobalLocale, string[]> = {
  en: ['Confirm current eligibility for your family structure', 'Request written medical scope, exclusions and refund terms', 'Obtain current legal advice on parentage and citizenship', 'Compare itemised medical, legal and travel costs', 'Plan documentation and return travel before treatment'],
  ko: ['가족 구성에 따른 최신 자격 조건 확인', '의료 범위·제외 항목·환불 조건을 서면으로 요청', '친권과 국적에 대한 최신 법률 자문 확보', '의료·법률·여행 비용을 항목별로 비교', '치료 전 서류와 귀국 절차 계획'],
  ja: ['家族構成に応じた最新の適格性を確認', '医療範囲・除外項目・返金条件を書面で確認', '親子関係と国籍について最新の法的助言を得る', '医療・法務・渡航費を明細で比較', '治療前に書類と帰国手続を計画'],
  zh: ['确认当前家庭结构是否符合准入条件', '书面确认医疗范围、排除项与退款条件', '就亲权与国籍取得当前法律意见', '逐项比较医疗、法律与旅行费用', '治疗前规划文件与返程手续']
};

export function getGlobalContent(locale: GlobalLocale): GlobalSafeContent {
  const content = structuredClone(referenceTranslations[locale]) as GlobalSafeContent;
  content.safeFacts = safeFacts[locale];
  content.trust = trust[locale];
  content.utility = utility[locale];

  const heroSubtitles = {
    en: 'A clearer way to build your family across borders — grounded in trusted facts, transparent pathways and coordinated professional review.',
    ko: '신뢰할 수 있는 사실, 투명한 경로, 조율된 전문가 검토를 바탕으로 국경을 넘어 가족을 이루는 더 명확한 방법입니다.',
    ja: '信頼できる事実、透明な経路、連携した専門家レビューを基に、国境を越えた家族づくりをより明確にします。',
    zh: '以可信事实、透明路径与协同专业复核为基础，让跨境家庭构建更清晰。'
  } satisfies Record<GlobalLocale, string>;
  content.hero.subtitle = heroSubtitles[locale];

  const safeNav = {
    en: [['Evidence first', 'Claims are separated from marketing'], ['Country by country', 'Rules are reviewed in context'], ['No outcome promises', 'Specialists confirm decisions']],
    ko: [['근거 우선', '홍보와 사실을 구분합니다'], ['국가별 검토', '규정은 상황에 맞춰 확인합니다'], ['결과를 약속하지 않음', '전문가가 결정을 확인합니다']],
    ja: [['エビデンス優先', '宣伝と事実を分けます'], ['国ごとに確認', '状況に応じて規則を確認'], ['結果を約束しない', '専門家が意思決定を確認']],
    zh: [['证据优先', '区分事实与宣传'], ['逐国复核', '结合具体情况确认规则'], ['不承诺结果', '由专业人员确认决定']]
  } satisfies Record<GlobalLocale, string[][]>;
  content.nav.stats = safeNav[locale];
  content.facts.intro = {
    en: 'Cross-border fertility care is widely used and complex. We start with published evidence so your questions are shaped by facts rather than advertising.',
    ko: '해외 생식 의료는 널리 이용되지만 복잡합니다. 광고가 아닌 사실을 바탕으로 질문할 수 있도록 공개된 근거부터 확인합니다.',
    ja: '国境を越える生殖医療は広く利用されていますが、複雑です。広告ではなく事実に基づいて質問できるよう、公開エビデンスから始めます。',
    zh: '跨境生殖医疗被广泛使用，也十分复杂。我们从公开证据出发，让您的问题建立在事实而非广告之上。'
  }[locale];

  const serviceDescriptions: Record<GlobalLocale, string[]> = {
    en: ['Compare IVF and ICSI pathways, clinic questions and treatment scope.', 'Understand donor models, screening questions and country-specific rules.', 'Map medical, legal, parentage and travel questions before choosing a programme.', 'Plan freezing, storage and cross-border transport questions.', 'Identify parentage and documentation issues for qualified legal review.', 'Organise records and questions for a qualified clinical review.'],
    ko: ['IVF·ICSI 경로, 병원 질문과 치료 범위를 비교합니다.', '기증 모델, 선별 질문과 국가별 규정을 이해합니다.', '프로그램 선택 전 의료·법률·친권·여행 질문을 정리합니다.', '동결, 보관 및 해외 운송 질문을 계획합니다.', '전문 법률 검토가 필요한 친권과 서류 문제를 파악합니다.', '전문 임상 검토를 위해 기록과 질문을 정리합니다.'],
    ja: ['IVF・ICSIの経路、施設への質問、治療範囲を比較します。', 'ドナー制度、選考項目、国ごとの規則を理解します。', 'プログラム選択前に医療・法務・親子関係・渡航の論点を整理します。', '凍結、保管、国際輸送の確認事項を計画します。', '専門家の法的レビューが必要な親子関係と書類の論点を特定します。', '専門的な臨床レビューに向けて記録と質問を整理します。'],
    zh: ['比较IVF与ICSI路径、诊所问题与治疗范围。', '了解捐赠模式、筛查问题与逐国规则。', '选择项目前梳理医疗、法律、亲权与旅行问题。', '规划冷冻、储存与跨境转运问题。', '识别需要专业法律复核的亲权与文件问题。', '为专业临床复核整理记录与问题。']
  };
  content.services.items = content.services.items.map((item, index) => ({ ...item, desc: serviceDescriptions[locale][index] }));
  content.services.intro = {
    en: 'Understand the medical, donor, legal and cross-border questions that shape each assisted-reproduction route.',
    ko: '각 보조생식 경로를 결정하는 의료, 기증, 법률 및 해외 이동 질문을 이해하세요.',
    ja: '各生殖補助医療の経路を形づくる、医療・ドナー・法務・国際移動の論点を理解します。',
    zh: '了解影响每条辅助生殖路径的医疗、捐赠、法律与跨境问题。'
  }[locale];

  content.pathways.intro = {
    en: 'Open each route to compare its usual stages and relative planning demands. Exact timing, eligibility and cost require individual review.',
    ko: '각 경로를 열어 일반적인 단계와 상대적 준비 부담을 비교하세요. 정확한 기간, 자격과 비용은 개별 검토가 필요합니다.',
    ja: '各経路を開き、一般的な段階と相対的な準備負担を比較します。正確な期間、適格性、費用は個別確認が必要です。',
    zh: '打开每条路径，比较常见阶段与相对规划要求。具体时间、资格与费用需要逐案复核。'
  }[locale];
  const pathwaySummaries: Record<GlobalLocale, string[]> = {
    en: [
      'International IVF or ICSI planning using intended-parent gametes, with protocol, clinic, travel and follow-up questions reviewed together.',
      'Egg, sperm or double donation, where donor screening, identity rules, eligibility and documentation vary by country.',
      'Gestational surrogacy requires coordinated medical and country-specific legal review, including parentage, citizenship and return travel.'
    ],
    ko: [
      '의도부모의 생식세포를 사용하는 해외 IVF·ICSI 계획으로, 프로토콜·병원·여행·추적관리 질문을 함께 검토합니다.',
      '난자·정자 또는 이중 기증 경로로, 기증자 선별·신원 규정·자격·서류가 국가마다 다릅니다.',
      '임신대리 경로는 친권·국적·귀국 절차를 포함한 의료 및 국가별 법률 검토의 조율이 필요합니다.'
    ],
    ja: [
      '意図する親の配偶子を用いる海外IVF・ICSI計画で、プロトコル、施設、渡航、フォローアップをまとめて確認します。',
      '卵子・精子または双方の提供で、ドナー選考、匿名性、適格性、書類規則は国により異なります。',
      '妊娠代行は、親子関係、国籍、帰国手続を含む医療と国別法務の連携レビューが必要です。'
    ],
    zh: [
      '使用意向父母配子的跨境IVF或ICSI规划，协同复核方案、诊所、旅行与随访问题。',
      '卵子、精子或双重捐赠路径；捐赠者筛查、身份规则、资格与文件因国家而异。',
      '妊娠代孕需要医疗与逐国法律协同复核，包括亲权、国籍与返程安排。'
    ]
  };
  content.pathways.items = content.pathways.items.map((item, index) => ({ ...item, summary: pathwaySummaries[locale][index] }));

  const vettingCopy = {
    en: ['Due-diligence framework', 'What to verify before choosing an international provider', 'Use six practical checks to compare providers. Licensing, outcomes, laboratory standards, parentage, welfare and price should each be confirmed with current documents.', 'A shortlist is a starting point for your own clinical, legal and financial review — not a guarantee of eligibility or outcome.'],
    ko: ['확인 프레임워크', '해외 기관 선택 전에 확인할 사항', '허가, 결과, 실험실 기준, 친권, 복지와 가격을 최신 문서로 확인하는 여섯 가지 실무 기준입니다.', '후보 목록은 임상·법률·재정 검토의 시작점이며 자격이나 결과를 보장하지 않습니다.'],
    ja: ['確認フレームワーク', '海外の機関を選ぶ前に確認すること', '許認可、成績、ラボ基準、親子関係、福祉、価格を最新書類で確認する6つの実務項目です。', '候補リストは臨床・法務・費用レビューの出発点であり、適格性や結果を保証しません。'],
    zh: ['尽调框架', '选择海外机构前需要确认什么', '用六项实用检查比较机构：执照、结果、实验室标准、亲权、福祉与价格都应通过当前文件确认。', '候选名单只是临床、法律与财务复核的起点，不保证资格或结果。']
  } satisfies Record<GlobalLocale, string[]>;
  content.vetting.label = vettingCopy[locale][0];
  content.vetting.heading = vettingCopy[locale][1];
  content.vetting.intro = vettingCopy[locale][2];
  content.vetting.note = vettingCopy[locale][3];

  const checklistLead = { en: 'Review point', ko: '검토 항목', ja: '確認項目', zh: '复核要点' }[locale];
  const countryAccess = {
    en: 'Current treatment access and eligibility require individual review',
    ko: '현재 치료 접근성과 자격은 개별 검토가 필요합니다',
    ja: '現在の治療アクセスと適格性は個別確認が必要です',
    zh: '当前治疗准入与资格需要逐案复核'
  }[locale];
  content.ecosystems.countries = content.ecosystems.countries.map((country) => ({
    ...country,
    headline: `${country.name} — ${content.ecosystems.label}`,
    pathways: countryAccess,
    checklist: countryOrientation[locale],
    stat: checklistLead
  }));

  const processIntro = {
    en: 'We organise the questions, comparisons and documents that qualified clinical and legal professionals need to review with you.',
    ko: '전문 임상 및 법률 담당자가 함께 검토할 수 있도록 질문, 비교 자료와 문서를 정리합니다.',
    ja: '専門的な臨床・法務担当者が確認できるよう、質問、比較、書類を整理します。',
    zh: '我们整理问题、比较与文件，交由合格的临床和法律专业人员与您共同复核。'
  }[locale];
  content.process.intro = processIntro;
  content.process.heading = {
    en: 'Structured guidance, from first question to continuity planning',
    ko: '첫 질문부터 연속성 계획까지 이어지는 구조화된 안내',
    ja: '最初の質問から継続計画まで、構造化されたガイダンス',
    zh: '从第一个问题到连续性规划的结构化引导'
  }[locale];
  const processSteps: Record<GlobalLocale, Array<{ t: string; d: string }>> = {
    en: [
      { t: 'Listening consultation', d: 'Clarify your history, family-building goals, budget parameters and open questions without assuming a destination.' },
      { t: 'Clinical questions', d: 'Organise records and treatment questions for review by an appropriately qualified fertility professional.' },
      { t: 'Country mapping', d: 'Compare access, parentage, citizenship, donor or carrier rules and travel requirements for the countries involved.' },
      { t: 'Provider comparison', d: 'Compare shortlisted providers using the same scope, exclusions, evidence questions and itemised cost categories.' },
      { t: 'Journey planning', d: 'Build a working checklist for scheduling, translation, travel, payments and documents.' },
      { t: 'Continuity planning', d: 'Prepare the questions and records needed for treatment follow-up, parentage, registration and return travel.' }
    ],
    ko: [
      { t: '경청 상담', d: '목적지를 먼저 정하지 않고 병력, 가족 계획 목표, 예산 범위와 미해결 질문을 정리합니다.' },
      { t: '임상 질문 정리', d: '자격 있는 생식 의료 전문가가 검토할 수 있도록 기록과 치료 질문을 정리합니다.' },
      { t: '국가 비교', d: '관련 국가의 접근성, 친권, 국적, 기증자·임신 담당자 규정과 여행 요건을 비교합니다.' },
      { t: '기관 비교', d: '동일한 범위, 제외 항목, 근거 질문과 비용 항목으로 후보 기관을 비교합니다.' },
      { t: '여정 계획', d: '일정, 통역, 여행, 결제와 서류를 위한 실무 체크리스트를 만듭니다.' },
      { t: '연속성 계획', d: '치료 추적, 친권, 등록과 귀국에 필요한 질문과 기록을 준비합니다.' }
    ],
    ja: [
      { t: 'ヒアリング相談', d: '渡航先を先に決めず、病歴、家族づくりの目標、予算、未解決事項を整理します。' },
      { t: '臨床質問の整理', d: '資格ある生殖医療専門家が確認できるよう、記録と治療上の質問を整理します。' },
      { t: '国別比較', d: '関係国のアクセス、親子関係、国籍、ドナー・妊娠担当者規則、渡航要件を比較します。' },
      { t: '機関比較', d: '同じ範囲、除外項目、エビデンス質問、費用項目で候補機関を比較します。' },
      { t: '旅程計画', d: '日程、通訳、渡航、支払、書類の実務チェックリストを作成します。' },
      { t: '継続計画', d: '治療後のフォロー、親子関係、登録、帰国に必要な質問と記録を準備します。' }
    ],
    zh: [
      { t: '倾听咨询', d: '不预设目的地，先厘清病史、家庭构建目标、预算范围与未解决问题。' },
      { t: '临床问题整理', d: '整理记录与治疗问题，供合格的生殖医疗专业人员复核。' },
      { t: '国家比较', d: '比较相关国家的准入、亲权、国籍、捐赠或孕母规则与旅行要求。' },
      { t: '机构比较', d: '按相同范围、排除项、证据问题与分项费用比较候选机构。' },
      { t: '旅程规划', d: '建立日程、翻译、旅行、付款与文件的工作清单。' },
      { t: '连续性规划', d: '准备治疗随访、亲权、登记与返程所需的问题和记录。' }
    ]
  };
  content.process.steps = processSteps[locale];
  content.process.quote = trust[locale].items[0].detail;
  content.process.quoteAttribution = trust[locale].items[0].title;

  const faqCopy: Record<GlobalLocale, Array<{ q: string; a: string }>> = {
    en: [
      { q: 'Can I access fertility treatment in another country?', a: 'Treatment access, parentage, citizenship and return travel depend on the countries involved and your circumstances. Confirm the current rules before paying a deposit or starting treatment.' },
      { q: 'How should I compare clinics and programmes?', a: 'Compare licensing, reported outcomes, laboratory standards, the exact treatment scope, exclusions, refund terms, legal steps and total likely cost. A headline package price is not enough.' },
      { q: 'How long can an international journey take?', a: 'Timing depends on diagnostics, donor or carrier matching, treatment, pregnancy, legal work and documents. Ask each provider for a stage-by-stage timeline and the assumptions behind it.' },
      { q: 'What does a guidance consultation include?', a: 'The consultation organises your priorities, open questions and the professional reviews you may need. Scope and any fees are confirmed before you decide whether to continue.' },
      { q: 'Can I request help after choosing a clinic?', a: 'Yes. You can request a structured review of your current plan, documents and unanswered questions. Clinical and legal conclusions must come from appropriately qualified professionals.' }
    ],
    ko: [
      { q: '다른 나라에서 생식 치료를 받을 수 있나요?', a: '치료 접근, 친권, 국적과 귀국 절차는 관련 국가와 개인 상황에 따라 달라집니다. 계약금 지불이나 치료 시작 전 최신 규정을 확인하세요.' },
      { q: '병원과 프로그램은 어떻게 비교해야 하나요?', a: '허가, 공개 성적, 실험실 기준, 정확한 치료 범위, 제외 항목, 환불 조건, 법률 절차와 총 예상비용을 비교하세요.' },
      { q: '해외 여정은 얼마나 걸리나요?', a: '진단, 기증자 또는 임신 담당자 매칭, 치료, 임신, 법률 및 서류에 따라 달라집니다. 단계별 일정과 전제를 요청하세요.' },
      { q: '가이드 상담에는 무엇이 포함되나요?', a: '우선순위, 미해결 질문과 필요한 전문 검토를 정리합니다. 계속 진행할지 결정하기 전에 범위와 비용을 확인합니다.' },
      { q: '이미 병원을 정했어도 도움을 요청할 수 있나요?', a: '네. 현재 계획, 문서와 미해결 질문을 구조적으로 검토하도록 요청할 수 있습니다. 임상 및 법률 결론은 자격 있는 전문가가 내려야 합니다.' }
    ],
    ja: [
      { q: '海外で生殖医療を受けられますか？', a: '治療アクセス、親子関係、国籍、帰国手続は関係する国と個別事情で異なります。支払いや治療開始前に最新規則を確認してください。' },
      { q: '施設やプログラムはどう比較しますか？', a: '許認可、公開成績、ラボ基準、治療範囲、除外、返金条件、法的手続、総費用を同じ条件で比較します。' },
      { q: '海外での家族づくりにはどのくらいかかりますか？', a: '診断、ドナーや妊娠担当者のマッチング、治療、妊娠、法務、書類により異なります。段階別の予定と前提を確認してください。' },
      { q: 'ガイダンス相談には何が含まれますか？', a: '優先事項、未解決の質問、必要な専門レビューを整理します。継続を決める前に範囲と費用を確認します。' },
      { q: 'すでに施設を選んでいても相談できますか？', a: 'はい。現在の計画、書類、未解決事項の構造的なレビューを依頼できます。臨床・法的結論は資格ある専門家が確認します。' }
    ],
    zh: [
      { q: '我可以去其他国家接受生殖治疗吗？', a: '治疗准入、亲权、国籍与返程取决于相关国家和个人情况。支付订金或开始治疗前，请确认当前规则。' },
      { q: '如何比较诊所与项目？', a: '应比较执照、公开结果、实验室标准、准确治疗范围、排除项、退款条件、法律步骤与预计总费用。仅看套餐价格不够。' },
      { q: '一次跨境家庭构建需要多久？', a: '时间取决于诊断、捐赠者或孕母匹配、治疗、妊娠、法律与文件。请机构提供逐阶段时间线及其前提。' },
      { q: '引导咨询包括什么？', a: '咨询会整理您的优先事项、未解决问题与可能需要的专业复核。继续前会先确认服务范围与费用。' },
      { q: '已经选择诊所后还能寻求帮助吗？', a: '可以。您可以请求对当前方案、文件和未解决问题进行结构化复核。临床与法律结论必须由合格专业人员确认。' }
    ]
  };
  content.faq.items = faqCopy[locale];

  const bookingCopy = {
    en: ['Tell us what you are exploring and when you would like to speak. We will contact you about the appropriate next step.', 'Your request is with our guidance team. We will contact you about next steps.'],
    ko: ['관심 있는 경로와 상담 희망일을 알려주세요. 적절한 다음 단계에 대해 연락드리겠습니다.', '요청이 가이드 팀에 전달되었습니다. 다음 단계에 대해 연락드리겠습니다.'],
    ja: ['検討中の経路と相談希望日をお知らせください。適切な次のステップについてご連絡します。', 'ご依頼はガイダンスチームに届きました。次のステップについてご連絡します。'],
    zh: ['告诉我们您正在探索的路径与希望沟通的日期。我们会联系您确认合适的下一步。', '您的请求已送达引导团队。我们会联系您确认下一步。']
  } satisfies Record<GlobalLocale, string[]>;
  content.booking.intro = bookingCopy[locale][0];
  content.booking.thanksBody = bookingCopy[locale][1];

  const footerDescriptions = {
    en: 'Clear guidance across international assisted-reproduction pathways. Medical and legal decisions are confirmed by qualified professionals.',
    ko: '해외 보조생식 경로에 대한 명확한 안내. 의료·법률 결정은 자격 있는 전문가가 확인합니다.',
    ja: '海外の生殖補助医療経路を明確にご案内します。医療・法的判断は資格ある専門家が確認します。',
    zh: '提供清晰的海外辅助生殖路径引导。医疗与法律决定由合格专业人员确认。'
  };
  content.footer.description = footerDescriptions[locale];

  return content;
}

export function routeForLocale(locale: GlobalLocale, hash = window.location.hash) {
  return `${localeRoutes[locale]}${hash || ''}`;
}
